export interface Error {
  details: ErrorDetails
  /**
   * Human readable error message
   */
  message: string
  providerCode?: null | string
  /**
   * Error type
   */
  type: ErrorType
}

/**
 * Booking access denied error details following the category/reason/recoverable convention
 *
 * Booking not found error details
 *
 * Hotel not found error details
 *
 * Input validation error details
 *
 * BookingFlow metadata not found error details
 *
 * Payment error details
 *
 * Provider error details
 *
 * OTP error details following the category/reason/recoverable convention
 *
 * Fallback for error details that don't match any known variant
 */
export interface ErrorDetails {
  /**
   * Booking access denied error category
   *
   * Error category
   *
   * Payment error category
   *
   * Provider error category
   *
   * OTP error category
   */
  category?: Category
  /**
   * Contains the ID of the missing booking flow metadata
   *
   * Provider error message (only for debug and tracking purposes).
   *
   * Provider error details (only for debug and tracking purposes).
   *
   * Error-specific details. Contents vary by category.
   */
  reason?: Reason
  /**
   * Indicates if the client can retry the request.
   *
   * A recoverable error means the request can be successful if the error is corrected and the
   * request resubmitted.
   */
  recoverable?: boolean
  /**
   * Booking ID
   */
  bookingId?: string
  /**
   * Hotel ID
   */
  hotelId?: string
  /**
   * Input validation errors
   */
  errors?: InputValidationError[]
  /**
   * User friendly message in the language indicated in the request.
   */
  message?: string
  /**
   * Provider error links. For recoverable errors in /book endpoint requests, it includes a
   * new /book endpoint link to be used when retry the booking.
   */
  links?: Link[]
}

/**
 * Booking access denied error category
 *
 * Error category
 *
 * Payment error category
 *
 * Provider error category
 *
 * OTP error category
 */
export enum Category {
  BookingAccessDenied = 'booking_access_denied',
  BookingFlowMetadataNotFound = 'booking_flow_metadata_not_found',
  BookingIncomplete = 'booking_incomplete',
  BookingNotCancelled = 'booking_not_cancelled',
  BookingNotFound = 'booking_not_found',
  CardNotSupported = 'card_not_supported',
  DataValidation = 'data_validation',
  DuplicateBooking = 'duplicate_booking',
  GenericCardError = 'generic_card_error',
  GenericFraudDeclined = 'generic_fraud_declined',
  GenericPaymentDeclined = 'generic_payment_declined',
  IncorrectCardNumber = 'incorrect_card_number',
  IncorrectCvc = 'incorrect_cvc',
  InsufficientFunds = 'insufficient_funds',
  InvalidEmail = 'invalid_email',
  MisusedCard = 'misused_card',
  OrderDeclined = 'order_declined',
  Other = 'other',
  OtpCooldown = 'otp_cooldown',
  OtpExpiredCode = 'otp_expired_code',
  OtpInvalidCode = 'otp_invalid_code',
  OtpMaxAttemptsExceeded = 'otp_max_attempts_exceeded',
  OtpNotFound = 'otp_not_found',
  OtpRateLimited = 'otp_rate_limited',
  OtpRequired = 'otp_required',
  Payment = 'payment',
  PaymentLimitExceeded = 'payment_limit_exceeded',
  PriceMismatch = 'price_mismatch',
  RateLimit = 'rate_limit',
  RequestError = 'request_error',
  SecurityBlocks = 'security_blocks',
  SoldOut = 'sold_out',
  TechnicalError = 'technical_error',
  TokenizationServiceUnavailable = 'tokenization_service_unavailable'
}

/**
 * Input validation error
 */
export interface InputValidationError {
  /**
   * Input validation error field
   */
  field: string
  /**
   * Input validation error validations
   */
  validations: Validation[]
}

/**
 * Input validation error validation
 */
export enum Validation {
  Cast = 'cast',
  DateRange = 'date_range',
  Exclusion = 'exclusion',
  Format = 'format',
  Inclusion = 'inclusion',
  Length = 'length',
  Number = 'number',
  Required = 'required'
}

/**
 * Provider error link
 */
export interface Link {
  /**
   * Provider error link href
   */
  href: string
  /**
   * Provider error link method
   */
  method: Method
  /**
   * Provider error link type
   */
  type: string
}

/**
 * Provider error link method
 */
export enum Method {
  Post = 'POST'
}

/**
 * Contains the ID of the missing booking flow metadata
 *
 * Provider error message (only for debug and tracking purposes).
 *
 * Provider error details (only for debug and tracking purposes).
 *
 * Reason details for data validation errors from provider
 *
 * Generic provider error details
 *
 * Error-specific details. Contents vary by category.
 */
export interface Reason {
  /**
   * Masked email address associated with the booking
   *
   * Masked email address where the OTP will be sent
   */
  maskedEmail?: string
  /**
   * ID of the missing booking flow metadata
   */
  id?: string
  /**
   * List of validation errors
   */
  errors?: DataValidationError[]
  /**
   * Top-level fields (empty when errors are nested)
   */
  fields?: any[]
  /**
   * General validation error message
   */
  message?: string
  /**
   * Validation error type identifier
   */
  type?: string
  /**
   * When true, indicates the payment was already captured (e.g. iDeal) and a refund will be
   * issued instead of a void. Only present for error type provider_booking_creation with APMs
   * that auto-capture during authorization.
   */
  paymentAlreadyCaptured?: boolean
  /**
   * ID of the last OTP request
   */
  lastOtpId?: string
  /**
   * Seconds until the next OTP request is allowed
   */
  retryAfter?: number
  /**
   * Current attempt number
   */
  attemptNumber?: number
  /**
   * Number of verification attempts remaining before lockout
   */
  remainingAttempts?: number
  /**
   * Booking ID
   */
  bookingId?: string
}

/**
 * Validation error item
 */
export interface DataValidationError {
  /**
   * Nested errors (if any)
   */
  errors?: any[]
  /**
   * Fields that caused the validation error
   */
  fields?: ValidationField[]
  /**
   * Human-readable error message for this specific validation error
   */
  message: string
  /**
   * Error type identifier
   */
  type: string
}

/**
 * Field information
 */
export interface ValidationField {
  /**
   * Field name
   */
  name: string
  /**
   * Field location in the request
   */
  type: FieldType
  /**
   * The invalid value that was provided
   */
  value?: boolean | number | null | string
}

/**
 * Field location in the request
 */
export enum FieldType {
  Body = 'body',
  Header = 'header',
  Path = 'path',
  Query = 'query'
}

/**
 * Error type
 */
export enum ErrorType {
  BookingAccessDenied = 'booking_access_denied',
  BookingAlreadyBeingFinalized = 'booking_already_being_finalized',
  BookingAlreadyCancelled = 'booking_already_cancelled',
  BookingAlreadyFinalized = 'booking_already_finalized',
  BookingCannotBeCancelled = 'booking_cannot_be_cancelled',
  BookingFlowMetadataNotFound = 'booking_flow_metadata_not_found',
  BookingNotFound = 'booking_not_found',
  BookingNotFrozen = 'booking_not_frozen',
  BookingStatusMetadataNotAvailable = 'booking_status_metadata_not_available',
  BookingUnauthorized = 'booking_unauthorized',
  BundleMetadata = 'bundle_metadata',
  BundleNotFound = 'bundle_not_found',
  CurrencyRates = 'currency_rates',
  Data = 'data',
  DataValidation = 'data_validation',
  HotelNotFound = 'hotel_not_found',
  InputValidation = 'input_validation',
  InvalidBookingID = 'invalid_booking_id',
  InvalidCurrency = 'invalid_currency',
  InvalidNotification = 'invalid_notification',
  OfferExpired = 'offer_expired',
  OfferMetadata = 'offer_metadata',
  OfferNotFound = 'offer_not_found',
  Otp = 'otp',
  Payment = 'payment',
  PaymentNotFound = 'payment_not_found',
  PriceIntegrity = 'price_integrity',
  Provider = 'provider',
  ProviderBookingCreation = 'provider_booking_creation',
  ProviderBookingNotFound = 'provider_booking_not_found',
  ProviderNotFound = 'provider_not_found',
  ProviderPreBook = 'provider_pre_book',
  UserLimitsReached = 'user_limits_reached'
}
